import { createContext, useContext, useMemo, useState } from 'react';
import { useBillsQuery } from '../api/getBills';
import { useWelcomeQuery } from '../api/getWelcomeDetails';

interface LastPayment {
  amount?: string;
  date?: string;
  policyNumber?: string;
}

// This context is to track the last payment amount and date since PAS will not reflect it immediately.
// We can show the user a banner if there is a mismatch between what they just paid and what is reflected in PAS.
const LastPaymentContext = createContext<{
  lastPayment: LastPayment;
  setLastPayment: React.Dispatch<React.SetStateAction<LastPayment>>;
  showPaymentDelayBanner: boolean;
}>({
  lastPayment: {},
  setLastPayment: () => {},
  showPaymentDelayBanner: false,
});

export const useLastPayment = () => useContext(LastPaymentContext);

export const LastPaymentProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { data: welcomeData } = useWelcomeQuery();
  const ready = welcomeData !== undefined;
  const { data: billingData, isFetching } = useBillsQuery(ready);
  const [lastPayment, setLastPayment] = useState<LastPayment>({});

  const showPaymentDelayBanner = useMemo(() => {
    if (
      !isFetching &&
      lastPayment.amount &&
      lastPayment.date &&
      lastPayment.policyNumber
    ) {
      const lastPaymentInBillsResponse = billingData?.payments.find(
        (payment) => payment.policyNumber === lastPayment.policyNumber
      );

      const differentPaymentDates =
        lastPaymentInBillsResponse?.lastPaymentDate !== lastPayment.date;
      const differentPaymentAmounts =
        lastPaymentInBillsResponse?.lastPaymentAmount !== lastPayment.amount;

      return differentPaymentDates || differentPaymentAmounts;
    }

    return false;
  }, [billingData, lastPayment, isFetching]);

  return (
    <LastPaymentContext.Provider
      value={{
        lastPayment,
        setLastPayment,
        showPaymentDelayBanner,
      }}
    >
      {children}
    </LastPaymentContext.Provider>
  );
};
